import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { questionnaireAPI } from '@/utils/api';
import ProductCard from '@/components/product/ProductCard';

const questions = [
  {
    id: 'species',
    title: '¿Buscas alimento para?',
    subtitle: 'Comencemos por saber para quién es',
    options: [
      { v: 'perro', l: 'Perro', icon: '🐶' },
      { v: 'gato', l: 'Gato', icon: '🐱' }
    ]
  },
  {
    id: 'life_stage',
    title: '¿En qué etapa de vida se encuentra?',
    subtitle: 'La edad determina sus necesidades nutricionales',
    options: [
      { v: 'cachorro', l: 'Cachorro o gatito (< 12 meses)', icon: '🐾' },
      { v: 'adulto', l: 'Adulto (1-7 años)', icon: '🦴' },
      { v: 'senior', l: 'Senior (> 7 años)', icon: '💚' }
    ]
  },
  {
    id: 'activity_level',
    title: '¿Cuál es su nivel de actividad?',
    subtitle: 'Su rutina diaria influye en el aporte energético',
    options: [
      { v: 'baja', l: 'Baja • Mayormente en casa', icon: '🛋️' },
      { v: 'media', l: 'Media • Paseos diarios', icon: '🚶' },
      { v: 'alta', l: 'Alta • Deportivo o trabajo', icon: '🏃' }
    ]
  },
  {
    id: 'food_format',
    title: '¿Qué formato prefieres?',
    subtitle: 'Seco, húmedo o combinado',
    options: [
      { v: 'seco', l: 'Alimento seco', icon: '🥣' },
      { v: 'húmedo', l: 'Alimento húmedo', icon: '🥫' }
    ]
  },
  {
    id: 'sensitivities',
    title: '¿Tiene alguna sensibilidad?',
    subtitle: 'Selecciona todas las que apliquen',
    multi: true,
    options: [
      { v: 'ninguna', l: 'Ninguna' },
      { v: 'piel-sensible', l: 'Piel sensible' },
      { v: 'estomago-sensible', l: 'Estómago sensible' },
      { v: 'alergias', l: 'Alergias alimentarias' },
      { v: 'sobrepeso', l: 'Sobrepeso' }
    ]
  },
  {
    id: 'budget_range',
    title: '¿Qué presupuesto manejas?',
    subtitle: 'Por presentación mensual aproximada',
    options: [
      { v: 'low', l: 'Económico', desc: 'Hasta $80.000' },
      { v: 'medium', l: 'Intermedio', desc: '$80.000 - $200.000' },
      { v: 'high', l: 'Premium sin límites', desc: 'Más de $200.000' }
    ]
  }
];

const Questionnaire = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    species: '',
    life_stage: '',
    activity_level: 'media',
    food_format: 'seco',
    sensitivities: [],
    budget_range: 'medium',
    weight_kg: 10,
    age_months: 24
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentQuestion = questions[step];
  const isLast = step === questions.length - 1;

  const handleNext = async () => {
    if (isLast) {
      // Submit
      setLoading(true);
      try {
        const res = await questionnaireAPI.recommend(answers);
        setResults(res);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => step > 0 && setStep(step - 1);

  const isNextDisabled = () => {
    const val = answers[currentQuestion.id];
    if (currentQuestion.multi) return false;
    return !val;
  };

  const handleOptionSelect = (value) => {
    if (currentQuestion.multi) {
      const current = answers[currentQuestion.id] || [];
      const newVal = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setAnswers({ ...answers, [currentQuestion.id]: newVal });
    } else {
      setAnswers({ ...answers, [currentQuestion.id]: value });
    }
  };

  if (results) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#2A5C43]/10 rounded-full px-4 py-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#2A5C43]" />
            <span className="text-xs uppercase tracking-widest text-[#2A5C43] font-semibold">Recomendaciones personalizadas</span>
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight mb-4">
            Estos son los alimentos ideales
          </h1>
          <p className="text-[#57534E] max-w-2xl mx-auto">
            Basado en las características de tu mascota, hemos seleccionado los mejores productos de nuestro catálogo.
          </p>
        </div>

        {results.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E7E5E4] max-w-lg mx-auto">
            <p className="text-lg text-[#1C1917] mb-4">No encontramos productos que coincidan exactamente.</p>
            <Button
              onClick={() => setResults(null)}
              className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full"
              data-testid="quiz-retry"
            >
              Intentar de nuevo
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {results.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
            <div className="text-center">
              <Button
                onClick={() => {
                  setStep(0);
                  setResults(null);
                  setAnswers({
                    species: '',
                    life_stage: '',
                    activity_level: 'media',
                    food_format: 'seco',
                    sensitivities: [],
                    budget_range: 'medium',
                    weight_kg: 10,
                    age_months: 24
                  });
                }}
                variant="outline"
                className="border-[#2A5C43] text-[#2A5C43] hover:bg-[#2A5C43] hover:text-white rounded-full"
                data-testid="quiz-restart"
              >
                Hacer el test de nuevo
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-gradient-to-br from-[#F7F2E8] via-[#FDFBF7] to-[#F7F2E8]">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">
              Pregunta {step + 1} de {questions.length}
            </span>
            <span className="text-xs text-[#57534E]">{Math.round(((step + 1) / questions.length) * 100)}%</span>
          </div>
          <div className="h-1 bg-[#E7E5E4] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#2A5C43] transition-all duration-500"
              style={{ width: `${((step + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="animate-fade-in-up">
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight mb-2">
            {currentQuestion.title}
          </h1>
          <p className="text-[#57534E] mb-8">{currentQuestion.subtitle}</p>

          <div className="space-y-3 mb-10">
            {currentQuestion.options.map((opt) => {
              const isSelected = currentQuestion.multi
                ? (answers[currentQuestion.id] || []).includes(opt.v)
                : answers[currentQuestion.id] === opt.v;

              return (
                <button
                  key={opt.v}
                  onClick={() => handleOptionSelect(opt.v)}
                  data-testid={`quiz-option-${opt.v}`}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-[#2A5C43] bg-white shadow-md'
                      : 'border-[#E7E5E4] bg-white/60 hover:border-[#8BA58F]'
                  }`}
                >
                  {opt.icon && <span className="text-3xl">{opt.icon}</span>}
                  <div className="flex-1">
                    <p className="font-display font-semibold text-[#1C1917]">{opt.l}</p>
                    {opt.desc && <p className="text-sm text-[#57534E]">{opt.desc}</p>}
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-[#2A5C43] flex items-center justify-center">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-3">
            <Button
              onClick={handleBack}
              disabled={step === 0}
              variant="outline"
              className="rounded-full border-[#E7E5E4] disabled:opacity-30"
              data-testid="quiz-back"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
            </Button>
            <Button
              onClick={handleNext}
              disabled={isNextDisabled() || loading}
              className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-8"
              data-testid="quiz-next"
            >
              {loading ? 'Analizando...' : isLast ? (
                <>Ver recomendaciones <ArrowRight className="w-4 h-4 ml-1" /></>
              ) : (
                <>Siguiente <ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
